<template>
    <div class="look-image-slider relative">
        <div
            class="absolute top-11.5 right-10 z-10 flex items-center lg:static lg:right-0 lg:mb-5 lg:justify-between lg:pr-20">
            <div class="hidden lg:block">
                <ShopwareBreadcrumbs />
            </div>
            <div>
                <button
                    type="button"
                    @click="addToWishlistSyncAndTriggerEvent(products)"
                    class="m-2 rounded-full bg-green-300 p-3 duration-300 hover:bg-green-500"
                    aria-label="Add to wishlist">
                    <HeartIcon class="size-5 stroke-2" />
                </button>
            </div>
        </div>
        <div
            v-if="!isLoading"
            class="pdp-image-slider overflow-hidden duration-300 lg:relative lg:flex lg:h-[700px] lg:flex-row-reverse">
            <Swiper
                class="relative w-[100vw] overflow-hidden !pb-6 lg:!absolute lg:left-0 lg:!mr-0 lg:mb-0 lg:max-h-[700px] lg:w-full"
                :modules="[SwiperAutoplay, SwiperFreeMode, SwiperNavigation, SwiperThumbs, SwiperPagination]"
                :navigation="false"
                :grabCursor="true"
                :pagination="{
                    clickable: true,
                }"
                :breakpoints="{
                    992: {
                        pagination: false,
                        navigation: {
                            nextEl: '.slider-next',
                            prevEl: '.slider-prev',
                        },
                    },
                }"
                :thumbs="{ swiper: thumbsSwiper }">
                <SwiperSlide
                    class="flex! items-center justify-center bg-white text-center lg:left-20 lg:max-h-[700px] lg:justify-end"
                    v-for="image in [...look.media].sort((a, b) => a.position - b.position)">
                    <ShopwareImg
                        @click="isOpen = true"
                        :class="[
                            'block h-auto max-h-full w-auto max-w-full lg:mr-20 lg:max-h-[700px]',
                            layout === 'looks'
                                ? 'lg:!mr-5 lg:!max-h-[500px] lg:px-7 2xl:!mr-20 2xl:!max-h-[700px]'
                                : '',
                        ]"
                        :src="image.media.url"
                        :alt="image.media.alt || look?.translated?.name || 'Product image'"
                        :width="700" />
                </SwiperSlide>
            </Swiper>
            <div
                class="hidden w-full lg:absolute lg:top-[32.5%] lg:!-right-1 lg:z-10 lg:flex lg:w-11/12 lg:justify-center 2xl:top-1/2 2xl:!right-0 2xl:w-full">
                <div class="justify-between lg:flex lg:w-8/12">
                    <button class="slider-prev border-2 border-black bg-white p-3 duration-300 hover:bg-green-500">
                        <NuxtImg
                            class="size-5 rotate-180"
                            :alt="snippets['headless.pdp.previousImage']"
                            src="/icons/arrow.svg" />
                    </button>
                    <button class="slider-next border-2 border-black bg-white p-3 duration-300 hover:bg-green-500">
                        <NuxtImg class="size-5" :alt="snippets['headless.pdp.nextImage']" src="/icons/arrow.svg" />
                    </button>
                </div>
            </div>
            <Swiper
                class="!z-10 !hidden h-96 lg:!absolute lg:top-0 lg:left-0 lg:!m-0 lg:!block lg:h-full lg:max-h-[700px] lg:w-24"
                @swiper="setThumbsSwiper"
                :slidesPerView="4.5"
                :space-between="10"
                :freeMode="true"
                :watchSlidesProgress="true"
                :breakpoints="{
                    992: {
                        direction: 'vertical',
                    },
                }"
                :modules="[SwiperAutoplay, SwiperFreeMode, SwiperNavigation, SwiperThumbs, SwiperZoom]">
                <SwiperSlide
                    class="cursor-pointer"
                    v-for="image in [...look.media].sort((a, b) => a.position - b.position)">
                    <ShopwareImg
                        class="swiper-zoom-container !h-[114px] !w-[76px] bg-white object-contain"
                        :src="image.media.url"
                        :alt="image.media.alt || look?.translated?.name || 'Product image'"
                        :width="76" />
                </SwiperSlide>
            </Swiper>
        </div>

        <div
            v-else
            class="pdp-image-slider animate-pulse overflow-hidden duration-300 lg:relative lg:flex lg:h-[700px] lg:flex-row-reverse">
            <div class="relative mb-5 w-full overflow-hidden lg:!absolute lg:left-0 lg:!mr-0 lg:mb-0 lg:max-h-[700px]">
                <div class="lg:!flex lg:max-h-[700px] lg:justify-end">
                    <div
                        class="h-96 w-full bg-gray-200 object-cover lg:mr-20 lg:!h-[900px] lg:max-h-[700px] lg:w-8/12"></div>
                </div>
            </div>
            <div class="hidden lg:absolute lg:top-1/2 lg:right-20 lg:z-10 lg:flex lg:w-8/12 lg:justify-between">
                <button class="slider-prev border-2 bg-gray-200 p-3"></button>
                <button class="slider-next border-2 bg-gray-200 p-3"></button>
            </div>
            <div class="h-max lg:!absolute lg:left-20 lg:!m-0 lg:w-24">
                <div class="hidden !h-32 cursor-pointer lg:block lg:!h-24">
                    <div class="mb-5 !h-32 w-full bg-gray-200 object-cover lg:!h-32"></div>
                    <div class="mb-5 !h-32 w-full bg-gray-200 object-cover lg:!h-32"></div>
                    <div class="!h-32 w-full bg-gray-200 object-cover lg:!h-32"></div>
                </div>
            </div>
        </div>
    </div>
    <ShopwareProductDetailLightbox :isOpen="isOpen" :product="look" @isOpen="closeLightbox" />
</template>

<script lang="ts" setup>
    import { HeartIcon } from '@heroicons/vue/24/outline';
    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;
    const wishlistSidebarStore = useWishlistSidebarStore();
    const { invokeWishlistSidebar } = wishlistSidebarStore;

    // Use unified wishlist for consistent behavior
    const { addToWishlist: addToUnifiedWishlist } = useUnifiedWishlist();

    const props = defineProps({
        look: Object,
        products: Object,
        layout: String,
    });

    const isLoading = ref(true);
    const isOpen = ref(false);
    const thumbsSwiper = ref(null);
    const isButtonHidden = ref(false);

    const { trackAddToWishlist } = useGTMEcommerce();

    function triggerEvent(product: any): void {
        trackAddToWishlist(product);
    }

    const setThumbsSwiper = swiper => {
        thumbsSwiper.value = swiper;
    };

    function closeLightbox() {
        isOpen.value = false;
        isButtonHidden.value = false;
    }
    async function addToWishlistSyncAndTriggerEvent(products: object) {
        const promises = Object.entries(products).map(async ([key, product]) => {
            await addToUnifiedWishlist(product.product.id);
        });

        await Promise.all(promises);

        // Trigger events after all products are added
        Object.entries(products).forEach(([key, product]) => {
            triggerEvent(product.product);
        });

        invokeWishlistSidebar();
    }

    onMounted(() => {
        isLoading.value = false;
    });
</script>
